import React, {useContext} from "react";
import {v4} from "uuid";

const defaultValue = {
    tableId: v4(),
    queryModel: {
        filter: {},
        paginationQuery: {
            pageNumber: 1,
            pageSize: 10
        },
        orderByItems: [],
        updateKey: ''
    },
    defaultFilter: {},
    selectedRows: [],
    expandedRowKeys: [],
    data: [],
    pagination: {

    },
    dataGridProps: {
        rowKey: 'id',
        columns: [],
        enableRowSelection: false,
        expandedRowRender: () => {},
        rowExpandable: false,
        antdTableProps: {},
        rowClassName: () => {},
        onRow: (record) => {}
    },
    renderItem: {
        recordId: '',
        key: ''
    }
}

export const DataGridActions = {
    ON_FILTER: 'ON_FILTER',
    ON_DEFAULT_FILTER: 'ON_DEFAULT_FILTER',
    ON_PAGE_AND_SORT: 'ON_PAGE_AND_SORT',
    ON_SELECTED_ROWS: 'ON_SELECTED_ROWS',
    SET_DATA: 'SET_DATA',
    SET_PAGINATION_RESULT: 'SET_PAGINATION_RESULT',
    SET_DATA_GRID_PROPS: 'SET_DATA_GRID_PROPS',
    RENDER_EXPANDED_ROW: 'RENDER_EXPANDED_ROW',
    SET_EXPANDED_ROW_KEYS: 'SET_EXPANDED_ROW_KEYS',
    REFRESH_DATA_GRID: 'REFRESH_DATA_GRID',
}

const DataGridContext = React.createContext(defaultValue)

function dataGridReducer(state, action)
{
    switch (action.type)
    {
        case DataGridActions.ON_FILTER: {
            return {
                ...state,
                selectedRows: [],
                queryModel: {
                    ...state.queryModel,
                    filter: action.filter,
                    paginationQuery: {
                        pageNumber: 1,
                        pageSize: state.queryModel.paginationQuery.pageSize
                    }
                }
            }
        }
        case DataGridActions.ON_DEFAULT_FILTER: {
            return {
                ...state,
                defaultFilter: action.defaultFilter
            }
        }
        case DataGridActions.ON_PAGE_AND_SORT: {
            return {
                ...state,
                queryModel: {
                    ...state.queryModel,
                    paginationQuery: action.paginationQuery,
                    orderByItems: action.orderByItems
                }
            }
        }
        case DataGridActions.ON_SELECTED_ROWS: {
            return {
                ...state,
                selectedRows: action.selectedRows
            }
        }
        case DataGridActions.SET_DATA: {
            return {
                ...state,
                data: action.data
            }
        }
        case DataGridActions.SET_PAGINATION_RESULT: {
            return {
                ...state,
                pagination: action.pagination
            }
        }
        case DataGridActions.SET_DATA_GRID_PROPS: {
            return {
                ...state,
                dataGridProps: action.dataGridProps
            }
        }
        case DataGridActions.RENDER_EXPANDED_ROW: {
            return {
                ...state,
                renderItem: {
                    recordId: action.recordId,
                    key: v4()
                }
            }
        }
        case DataGridActions.REFRESH_DATA_GRID: {
            return {
                ...state,
                queryModel: {
                    ...state.queryModel,
                    updateKey: v4()
                }
            }
        }
        default: {
            return defaultValue
        }
    }
}

function DataGridProvider({children, name, defaultFilter})
{
    const [dataGrid, dispatch] = React.useReducer(dataGridReducer, {
        ...defaultValue,
        queryModel: {
            ...defaultValue.queryModel,
            filter: defaultFilter || {}
        }
    });

    const value = {dataGrid, dispatch};

    return (
        <DataGridContext.Provider value={value}>
            {children}
        </DataGridContext.Provider>
    )
}

function useDataGrid()
{
    const context = React.useContext(DataGridContext);

    if(!context)
        throw new Error('Data Grid Provider Error')

    const {dataGrid, dispatch} = context;

    const onFilter = (filter) => {
        dispatch({
            type: DataGridActions.ON_FILTER,
            filter
        })
    }

    const onPaginationAndSort = (paginationQuery, orderByItems) => {
        dispatch({
            type: DataGridActions.ON_PAGE_AND_SORT,
            paginationQuery,
            orderByItems
        })
    }

    const setSelectedRows = (selectedRows) => {
        dispatch({
            type: DataGridActions.ON_SELECTED_ROWS,
            selectedRows
        })
    }

    const setData = (data) => {
        dispatch({
            type: DataGridActions.SET_DATA,
            data
        })
    }

    const setDataGridProps = ({rowKey, columns, rowExpandable, expandedRowRender, enableRowSelection, antdTableProps, rowClassName, onRow}) => {
        dispatch({
            type: DataGridActions.SET_DATA_GRID_PROPS,
            dataGridProps: {
                ...dataGrid.dataGridProps,
                rowKey,
                columns,
                enableRowSelection,
                antdTableProps,
                rowExpandable,
                expandedRowRender,
                rowClassName,
                onRow
            }
        })
    }

    const setPaginationResult = (pagination) => {
        dispatch({
            type: DataGridActions.SET_PAGINATION_RESULT,
            pagination
        })
    }

    const renderExpandedRow = (recordId) => {
        dispatch({
            type: DataGridActions.RENDER_EXPANDED_ROW,
            recordId
        })
    }

    const refreshDataGrid = () => {
        dispatch({
            type: DataGridActions.REFRESH_DATA_GRID,
        })
    }

    const setRowColor = (id, color) => {
        const table = document.getElementById(dataGrid.tableId);
        if(table)
        {
            let row = table.querySelectorAll(`[data-row-key="${id}"]`)[0];
            let tmp = document.createElement('div');

            tmp.style.backgroundColor = color;
            if(row.style.backgroundColor === tmp.style.backgroundColor)
            {
                color = ''
            }

            let rows = table.querySelectorAll(`[data-row-key]`);
            rows.forEach(r => {
                r.style.backgroundColor = ''
            })


            row.style.backgroundColor = color
        }

    }

    return {
        tableId: dataGrid.tableId,
        queryModel: dataGrid.queryModel,
        resultModel: {
            pagination: dataGrid.pagination,
            data: dataGrid.data,
        },
        dataGridProps: dataGrid.dataGridProps,
        selectedRows: dataGrid.selectedRows,
        renderItem: dataGrid.renderItem,
        onFilter,
        onPaginationAndSort,
        setSelectedRows,
        setData,
        setPaginationResult,
        setDataGridProps,
        renderExpandedRow,
        refreshDataGrid,
        setRowColor
    }
}

export {DataGridProvider, useDataGrid}
